import React, { useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import AlertBar from "../common/alert";
import ReceiptHeader from "./ReceiptHeader";
import { useReceiptData } from "./handler/useReceiptData";
import { useCancelReceipt } from "./handler/useCancelReceipt";
import CancelledCompleteComponent from "./CancelledCompleteComponent";
import LeftCard from "./LeftCard";
import RightCard from "./RightCard";
import { mapReceiptStatusToOrderStatus } from "./handler/receiptStatus.helper";
import PaymentPage from "../PaymentPage/PaymentPage";
import QRPaymentPage from "../PaymentPage/qr-payments/QRPaymentPage";
import MobilePaymentPage from "../PaymentPage/mobile-payment/MobilePaymentPage";

interface WebReceiptProps {
  /**
   * When true, hides the payment card and the cancel-trip button. Used by the
   * app-shared trip-tracking route (/app-receipt/:id) — DASH-5282.
   */
  hidePaymentAndCancel?: boolean;
}

const WebReceipt: React.FC<WebReceiptProps> = ({
  hidePaymentAndCancel = false,
}) => {
  const [alert, setAlert] = useState({
    show: false,
    severity: "info",
    title: "",
    message: "",
  });
  const { data, status, refetch } = useReceiptData(setAlert);
  const cancelMutation = useCancelReceipt(setAlert, {
    onSuccess: () => {
      refetch();
    },
  });
  const orderStatus = mapReceiptStatusToOrderStatus(data?.status ?? "");
  // const orderStatus = "cancelled" as OrderStatus;

  const isCenterCard =
    orderStatus === "completed" || orderStatus === "cancelled";

  const showPaymentPage = ["PENDING_PAYMENT"].includes(data?.status ?? "");
  const isQrPayment = data?.type === "QR_PAYMENT";
  const isMobilePayment = data?.type === "MOBILE_PAYMENT";

  if (isQrPayment) {
    return <QRPaymentPage />;
  }

  if (isMobilePayment) {
    return <MobilePaymentPage />;
  }

  if (showPaymentPage) {
    return <PaymentPage />;
  }

  return (
    <div className="bg-gray-50 min-h-screen relative">
      {status === "success" && data ? (
        <>
          <div className="fixed top-0 left-0 w-full z-10">
            <ReceiptHeader data={data} orderStatus={orderStatus} />
          </div>

          {isCenterCard ? (
            <div className="flex flex-col items-center min-h-screen">
              <div className="w-full max-w-2xl mt-[55px] md:mt-[90px]">
                <CancelledCompleteComponent
                  orderStatus={orderStatus}
                  data={data}
                />
              </div>
            </div>
          ) : (
            <div className="pt-20 flex flex-col md:flex-row min-h-screen md:px-5 md:gap-5">
              {/* Map at top on mobile, right on desktop */}
              <div className="w-full md:w-1/2 md:order-2 flex-shrink-0 md:mt-5 mb-5">
                <div className="h-80 md:h-full">
                  <RightCard
                    className="w-full h-full"
                    data={data}
                    orderStatus={orderStatus}
                  />
                </div>
              </div>
              {/* Details scrollable on mobile, left on desktop */}
              <div className="flex-1 overflow-y-auto md:w-1/2 md:order-1 md:mt-5 mb-5">
                <LeftCard
                  className="w-full h-full"
                  orderStatus={orderStatus}
                  data={data}
                  onCancel={() => cancelMutation.mutate()}
                  hidePaymentAndCancel={hidePaymentAndCancel}
                />
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center text-gray-400">
          Loading...
        </div>
      )}

      <Snackbar
        open={!!alert.show}
        sx={{ width: "95%" }}
        autoHideDuration={5000}
        onClose={() => setAlert({ ...alert, show: false })}
      >
        <AlertBar
          severity={alert.severity as any}
          show={!!alert.show}
          title={alert.title}
          message={typeof alert.message === "string" ? alert.message : ""}
          onClose={() => setAlert({ ...alert, show: false })}
        />
      </Snackbar>
    </div>
  );
};

export default WebReceipt;
